// Component for editing an occasion
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

const EditOccasion = () => {
    const [outfits, setOutfits] = useState([]); // Store available outfits
    const [formData, setFormData] = useState({
        occasion_name: '',
        location: '',
        description: '',
        outfits: []
    });

    // Search state variables
    const [outfitNameSearchTerm, setOutfitNameSearchTerm] = useState('');
    const [occasionSearchTerm, setOccasionSearchTerm] = useState('');
    const [clothingItemSearchTerm, setClothingItemSearchTerm] = useState('');
    const [expandedOutfits, setExpandedOutfits] = useState({});

    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        fetchOccasion(); // Load occasion data
        fetchOutfits(); // Load outfits data
    }, []);

    const fetchOccasion = async () => {
        try {
            const response = await axios.get(`http://localhost:3000/api/occasions/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const occasion = response.data;
            setFormData({
                occasion_name: occasion.occasion_name || '',
                location: occasion.location ? occasion.location.join(', ') : '',
                description: occasion.description || '',
                outfits: occasion.outfits ? occasion.outfits.map(outfit => outfit._id) : []  // Ensure outfits are populated here
            });
        } catch (error) {
            console.error('Error fetching occasion:', error.response ? error.response.data : error.message);
        }
    };

    const fetchOutfits = async () => {
        try {
            const response = await axios.get('http://localhost:3000/api/outfits', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setOutfits(response.data);
        } catch (error) {
            console.error('Error fetching outfits:', error.response ? error.response.data : error.message);
        }
    };

    const handleOutfitSelect = (outfitId) => {
        setFormData(prevData => ({
            ...prevData,
            outfits: prevData.outfits.includes(outfitId)
                ? prevData.outfits.filter(id => id !== outfitId)
                : [...prevData.outfits, outfitId]
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const updatedOccasion = {
            occasion_name: formData.occasion_name,
            location: formData.location.split(',').map(loc => loc.trim()),
            description: formData.description,
            outfits: formData.outfits
        };

        try {
            await axios.put(`http://localhost:3000/api/occasions/${id}`, updatedOccasion, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            navigate('/occasions');  // Redirect to Occasions page after update
        } catch (error) {
            console.error('Error updating occasion:', error.response ? error.response.data : error.message);
            alert('Error updating occasion. Please try again.');
        }
    };

    const toggleOutfitDetails = (outfitId) => {
        setExpandedOutfits(prevState => ({
            ...prevState,
            [outfitId]: !prevState[outfitId]
        }));
    };

    // Filter outfits based on search terms
    const filteredOutfits = outfits.filter(outfit => {
        const matchesOutfitName = outfit.outfit_name.toLowerCase().includes(outfitNameSearchTerm.toLowerCase());
        const matchesOccasions = outfit.occasion.some(occ => occ.toLowerCase().includes(occasionSearchTerm.toLowerCase()));
        const matchesClothingItems = outfit.clothing_items.some(cloth => 
            cloth.cloth_name.toLowerCase().includes(clothingItemSearchTerm.toLowerCase()) ||
            cloth.color.some(col => col.toLowerCase().includes(clothingItemSearchTerm.toLowerCase())) || 
            cloth.category.some(cat => cat.toLowerCase().includes(clothingItemSearchTerm.toLowerCase())) ||
            cloth.season.some(sea => sea.toLowerCase().includes(clothingItemSearchTerm.toLowerCase()))
        );

        return matchesOutfitName && matchesOccasions && matchesClothingItems;
    });

    return (
        <div className="edit-occasion-container">
            <h2 style={{ textAlign: 'center' }}><strong>𝐸𝒹𝒾𝓉 𝒪𝒸𝒸𝒶𝓈𝒾𝑜𝓃</strong></h2>
            <form onSubmit={handleSubmit}>
                <label>
                    <strong>Name:</strong>
                    <input
                        type="text"
                        name="occasion_name"
                        value={formData.occasion_name}
                        onChange={handleChange}
                        placeholder="Enter occasion name"
                    />
                </label>
                <label>
                    <strong>Location:</strong>
                    <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="e.g. Gym, Park, Home (separate with commas)"
                    />
                </label>
                <label>
                    <strong>Description:</strong>
                    <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Short Occasion Description (optional)"
                    />
                </label>

                <div className="outfit-search-section">
                    <h3><strong>𝒮𝑒𝒶𝓇𝒸𝒽 & 𝒮𝑒𝓁𝑒𝒸𝓉 𝒪𝓊𝓉𝒻𝒾𝓉𝓈:</strong></h3>
                    <input
                        type="text"
                        placeholder="Search by outfit name"
                        value={outfitNameSearchTerm}
                        onChange={(e) => setOutfitNameSearchTerm(e.target.value)}
                    />
                    <input
                        type="text"
                        placeholder="Search by occasions"
                        value={occasionSearchTerm}
                        onChange={(e) => setOccasionSearchTerm(e.target.value)}
                    />
                    <input
                        type="text"
                        placeholder="Search by clothing items"
                        value={clothingItemSearchTerm}
                        onChange={(e) => setClothingItemSearchTerm(e.target.value)}
                    />

                    {filteredOutfits.map((outfit) => (
                        <div 
                            key={outfit._id} 
                            className={`outfit-selection ${formData.outfits.includes(outfit._id) ? 'selected' : ''}`} 
                            onClick={() => {
                                handleOutfitSelect(outfit._id);
                                toggleOutfitDetails(outfit._id);
                            }}
                        >
                            <strong>{outfit.outfit_name}</strong>
                            {expandedOutfits[outfit._id] && (
                                <div className="clothing-items-dropdown">
                                    {outfit.clothing_items.map((cloth) => (
                                        <div key={cloth._id} className="clothing-item">
                                            <img crossorigin="anonymous" src={`http://localhost:3000/${cloth.image_url}`} alt={cloth.cloth_name} />
                                            <p>{cloth.cloth_name}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <button type="submit" className="edit-btn"><i className="fa-solid fa-upload"></i><strong>𝒰𝓅𝒹𝒶𝓉𝑒 𝒪𝒸𝒸𝒶𝓈𝒾𝑜𝓃</strong></button>
            </form>
        </div>
    );
};

export default EditOccasion;